import Tooltip from '@mui/material/Tooltip';

import { useTranslate } from 'src/locales';
import { useGetCallListMembershipsQuery } from 'src/services/call-lists/call-lists.service';
import { CallListPersonType, CallListEntryStatus } from 'src/services/call-lists/call-lists.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { useCallListsEnabled } from '../hooks/use-call-lists-enabled';

// ----------------------------------------------------------------------

type Props = {
  personType: CallListPersonType;
  personId?: string;
};

/** "On your call list · Attempt 3 of 4" on a profile, when the viewer has this person on an active list. */
export default function CallListStatusChip({ personType, personId }: Props) {
  const { t } = useTranslate();
  const enabled = useCallListsEnabled();

  const { currentData } = useGetCallListMembershipsQuery(
    { entityType: personType, entityId: personId as string },
    { skip: !enabled || !personId }
  );

  const active = (currentData?.mine ?? []).filter((membership) => membership.status === CallListEntryStatus.ACTIVE);
  if (!active.length) return null;

  const [first] = active;
  const others = (currentData?.others ?? []).filter((shared) => !shared.is_mine);

  return (
    <Tooltip title={active.map((membership) => membership.call_list_name).join(', ')}>
      <Label variant="soft" color="info" startIcon={<Iconify icon="solar:phone-calling-rounded-bold" />} data-testid="call-list-status-chip">
        {t('call_lists.profile.on_your_list', { current: first.attempt_number, max: first.max_attempts })}
        {others.length > 0 && ` · ${t('call_lists.profile.also_with', { count: others.length })}`}
      </Label>
    </Tooltip>
  );
}
